import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  deleteProjet,
  getProjets,
  updateProjet,
} from '../../redux/projets/projetActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { NavLink, useNavigate } from 'react-router-dom'
import LaunchIcon from '@mui/icons-material/Launch'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import DeleteButton from '../../components/common/DeleteButton'
import PaginationComp from '../../components/common/pagination'
import EntitySelector from '../../components/common/EntitySelector'
import UseAlert from '../../components/common/alert'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import { formatTitle } from '../../utils/formatTitle'
import { systemLabel } from '../../utils/systemLabel'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import i18n from '../../i18n'
import { formatCurrency } from '../../utils/formatNumbers'
import { Projet } from '../../types/projetTypes'
import { getPipelinebyMission } from '../../redux/pipelines/pipelineActions'
import {
  ListPageShell,
  listPageChipSx,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import { priorityLabelsTranslations } from '../../utils/constants'
import '../../components/common/ListPageTable.css'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { ListPageDateCell } from '../../components/common/ListPageCells'
import { onClickableTableRow } from '../../utils/tableRowClick'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { registerHeaderCount } from '../../utils/listCounts'

type StatusFilter = 'all' | 'todo' | 'in_progress' | 'done'

const STATUS_VALUE: Record<Exclude<StatusFilter, 'all'>, string> = {
  todo: 'A faire',
  in_progress: 'En cours',
  done: 'Terminé',
}

const PRIORITY_PILL: Record<number, { bg: string; color: string; label: string }> = {
  1: { bg: '#FAE6E3', color: '#96271B', label: 'Critical' },
  2: { bg: '#FAEFDA', color: '#84540A', label: 'High' },
  3: { bg: '#E6EFF8', color: '#22548A', label: 'Medium' },
  4: { bg: '#E1F4F0', color: '#076B5C', label: 'Low' },
}

const STATUS_PILL: Record<string, { bg: string; color: string }> = {
  'A faire': { bg: '#E1F4F0', color: '#076B5C' },
  'En cours': { bg: '#E6EFF8', color: '#22548A' },
  Terminé: { bg: '#E1F4F0', color: '#076B5C' },
  Suspendu: { bg: '#EAF0F0', color: '#5F7484' },
  'En attente': { bg: '#FAEFDA', color: '#84540A' },
}

function Pill({
  label,
  bg,
  color,
}: {
  label: string
  bg: string
  color: string
}) {
  return (
    <span className="incyia-pill" style={{ background: bg, color }}>
      <span className="incyia-pill-dot" />
      {label}
    </span>
  )
}

function DateCell({
  value,
  locale,
}: {
  value?: string | Date
  locale: string
}) {
  return <ListPageDateCell value={value} locale={locale} />
}

export default function ProjetListe() {
  const { t } = useTranslation()
  const locale = i18n.language
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [loadingPipelines, setLoadingPipelines] = useState(false)
  const [pipelinesMissionId, setPipelinesMissionId] = useState<string | null>(
    null
  )
  const [activeSelectProjetId, setActiveSelectProjetId] = useState<
    string | null
  >(null)

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const pipelinesList = useSelector(
    (state: RootState) => state.pipeline.PipelineResponse?.pipelines
  )
  const projetsList = useSelector(
    (state: RootState) => state.projet.ProjetResponse
  )
  const loading = useSelector((state: RootState) => state.projet.loading)
  const showLoader = useTableLoadingGate(loading, [page, pageSize, search])
  const roles = useSelector((state: RootState) => state.auth.roles)
  const currency = useSelector(selectSpecifications)[0]?.Currency

  useEffect(() => {
    dispatch(getProjets({ search, page, pageSize }))
  }, [dispatch, page, pageSize, search])

  const filteredProjects = useMemo(() => {
    const rows = projetsList?.projets ?? []
    if (statusFilter === 'all') return rows
    const target = STATUS_VALUE[statusFilter]
    return rows.filter(
      (p: Projet) =>
        (p.Status || '').trim().toLowerCase() === target.toLowerCase()
    )
  }, [projetsList?.projets, statusFilter])

  const pipelinesForActiveMission = useMemo(() => {
    if (!pipelinesMissionId || !pipelinesList?.length) return []
    return pipelinesList.filter((pipeline) => {
      if (!pipeline.MissionId) return true
      return String(pipeline.MissionId) === String(pipelinesMissionId)
    })
  }, [pipelinesList, pipelinesMissionId])

  const canAdd =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const canEdit =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const canDelete =
    hasRole(roles, UserRole.RSSI) || hasRole(roles, UserRole.ADMIN)

  const fetchPipelinesForMission = async (projet: Projet) => {
    if (!projet.MissionId) {
      setPipelinesMissionId(null)
      return
    }
    try {
      setActiveSelectProjetId(projet._id || null)
      setLoadingPipelines(true)
      setPipelinesMissionId(projet.MissionId)
      await dispatch(
        getPipelinebyMission({
          MissionId: projet.MissionId,
          page: 1,
          pageSize: 200,
        })
      )
    } catch (error) {
      console.error('Error fetching pipelines:', error)
      setPipelinesMissionId(null)
    } finally {
      setLoadingPipelines(false)
    }
  }

  const handlePipelineSelect = async (
    selectedPipelineId: string,
    projet: Projet
  ) => {
    try {
      const selectedPipeline = pipelinesForActiveMission.find(
        (p) => p._id === selectedPipelineId
      )
      if (
        selectedPipeline?.MissionId &&
        projet.MissionId &&
        String(selectedPipeline.MissionId) !== String(projet.MissionId)
      ) {
        setAlert({
          open: true,
          message: t('alerts.projectUpdate.pipelineMissionMismatch', {
            defaultValue:
              'This pipeline belongs to another mission and cannot be linked.',
          }),
          severity: 'error',
        })
        return
      }

      await dispatch(
        updateProjet({
          updatedProjet: {
            ...projet,
            PipelineId: selectedPipelineId,
            PipelineName: selectedPipeline?.PipelineName,
          },
          _id: projet._id || '',
        })
      )
      setAlert({
        open: true,
        message: t('alerts.projectUpdate.success'),
        severity: 'success',
      })
      dispatch(getProjets({ search, page, pageSize }))
    } catch {
      setAlert({
        open: true,
        message: t('Error updating project'),
        severity: 'error',
      })
    }
  }

  const handleDeleteClick = async (projetId: string) => {
    try {
      dispatch(deleteProjet(projetId))
      setAlert({
        open: true,
        message: 'delete successfully!',
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: 'Error deleting action. Please try again later.',
        severity: 'error',
      })
    }
  }

  const statusChips: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t('listPages.all') },
    { key: 'todo', label: t('listPages.toDo') },
    { key: 'in_progress', label: t('listPages.inProgress') },
    { key: 'done', label: t('listPages.done') },
  ]

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ListPageShell
        eyebrow={t('managment')}
        title={t('Projects')}
        titleTourId="projects-page-header"
        subtitle={t('listPages.projectsSub')}
        addLabel={canAdd ? t('buttons.addProject') : undefined}
        onAdd={canAdd ? () => navigate('/addprojet') : undefined}
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder={t('filterByName')}
        countLabel={t('listPages.projectsCount', {
          count: registerHeaderCount(
            projetsList?.total ?? 0,
            filteredProjects.length,
            statusFilter === 'all'
          ),
        })}
        chips={
          <>
            {statusChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                style={listPageChipSx(statusFilter === chip.key)}
                onClick={() => setStatusFilter(chip.key)}
              >
                {chip.label}
              </button>
            ))}
          </>
        }
        footer={
          (projetsList?.totalPages as number) > 0 && statusFilter === 'all' ? (
            <PaginationComp
              page={page}
              pageSize={pageSize}
              onChange={(nextPage, newSize) => {
                setPage(nextPage)
                setPageSize(newSize)
              }}
              totalItems={projetsList?.totalPages as number}
            />
          ) : undefined
        }
      >
        <div className="incyia-table-wrap">
          {showLoader ? (
            <TableLoadingState />
          ) : filteredProjects.length === 0 ? (
            <TableEmptyState />
          ) : (
            <table className="incyia-table">
              <thead>
                <tr>
                  <th>{t('Project Name')}</th>
                  <th>{t('Pipeline')}</th>
                  <th className="incyia-num">{t('Budget')}</th>
                  <th>{t('Project Type')}</th>
                  <th>{t('Description')}</th>
                  <th>{t('Start Date')}</th>
                  <th>{t('End Date')}</th>
                  <th>{t('Priority')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Source')}</th>
                  <th>{t('Mission Name')}</th>
                  <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project: Projet) => {
                  const priority = Number(project.Priority)
                  const priorityStyle =
                    PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
                  const statusStyle = STATUS_PILL[project.Status || ''] ?? {
                    bg: '#EAF0F0',
                    color: '#5F7484',
                  }

                  return (
                    <tr key={project._id} onClick={onClickableTableRow}>
                      <td className="incyia-col-pinned">
                        <NavLink
                          to={`/mission/${project.MissionId}/projet/${project._id}/`}
                          className="incyia-name-link"
                        >
                          <ListPageDescription
                            value={
                              project.ProjetName
                                ? formatTitle(project.ProjetName)
                                : undefined
                            }
                            tone="primary"
                          />
                        </NavLink>
                      </td>
                      <td>
                        <EntitySelector
                          value={project.PipelineName}
                          linkTo={`/mission/${project.MissionId}/pipeline/${project.PipelineId}`}
                          rowData={project}
                          options={
                            loadingPipelines &&
                            activeSelectProjetId === project._id
                              ? []
                              : pipelinesMissionId &&
                                  project.MissionId &&
                                  String(pipelinesMissionId) ===
                                    String(project.MissionId)
                                ? pipelinesForActiveMission
                                : []
                          }
                          optionLabelKey="PipelineName"
                          placeholder={
                            project.MissionId
                              ? t('Select')
                              : t('entitySelector.noMission', {
                                  defaultValue: 'No mission',
                                })
                          }
                          entityName="pipeline"
                          onSelect={handlePipelineSelect}
                          onFetchOptions={fetchPipelinesForMission}
                          isLoading={
                            loadingPipelines &&
                            activeSelectProjetId === project._id
                          }
                          disabled={!project.MissionId}
                        />
                      </td>
                      <td>
                        <span className="incyia-money">
                          {formatCurrency(project.Budget ?? 0, currency, {
                            locale,
                            useIntl: true,
                          })}
                        </span>
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription
                          value={
                            project.TypeProjet
                              ? systemLabel(t, 'projectType', project.TypeProjet)
                              : undefined
                          }
                        />
                      </td>
                      <td className="incyia-wrap">
                        <ListPageDescription value={project.Description} />
                      </td>
                      <td>
                        <DateCell value={project.DateDebut} locale={locale} />
                      </td>
                      <td>
                        <DateCell value={project.DateFin} locale={locale} />
                      </td>
                      <td>
                        <Pill
                          label={
                            t(`priority.${priority}`, {
                              defaultValue:
                                priorityLabelsTranslations[priority] ||
                                priorityStyle.label,
                            }) || priorityStyle.label
                          }
                          bg={priorityStyle.bg}
                          color={priorityStyle.color}
                        />
                      </td>
                      <td>
                        <Pill
                          label={t(project.Status || '', {
                            defaultValue: project.Status || '—',
                          })}
                          bg={statusStyle.bg}
                          color={statusStyle.color}
                        />
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={project.Source} />
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={project.MissionName} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="incyia-row-actions">
                          <button
                            type="button"
                            title={t('listPages.openAction')}
                            onClick={() =>
                              navigate(
                                `/mission/${project.MissionId}/projet/${project._id}/`
                              )
                            }
                            style={listPageIconBtnStyle}
                          >
                            <LaunchIcon sx={{ fontSize: 15 }} />
                          </button>
                          {canEdit && (
                            <button
                              type="button"
                              title={t('listPages.edit')}
                              onClick={() =>
                                navigate(`/projet/${project._id}`)
                              }
                              style={listPageIconBtnStyle}
                            >
                              <DriveFileRenameOutlineOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            </button>
                          )}
                          {canDelete && (
                            <DeleteButton
                              onDelete={() =>
                                handleDeleteClick(project._id as string)
                              }
                              item={{
                                entityType: t('projects', {
                                  defaultValue: 'Project',
                                }),
                                name: project.ProjetName
                                  ? formatTitle(project.ProjetName)
                                  : undefined,
                                details: [
                                  {
                                    label: t('deleteConfirm.fields.mission'),
                                    value: project.MissionName
                                      ? formatTitle(project.MissionName)
                                      : '—',
                                  },
                                  {
                                    label: t('deleteConfirm.fields.status'),
                                    value: project.Status || '—',
                                  },
                                ],
                              }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </ListPageShell>
    </>
  )
}
